import React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { getApiErrorMessage } from "@maeiz/core";
import { settingsApi, type OwnerSettings } from "@api/modules/settings.api";
import { useToast } from "@hooks/useToast";
import { useAuthStore } from "@stores/auth/authStore";
import { useCurrencyStore } from "@stores/currency";
import { useLanguageStore } from "@stores/language";
import {
    normalizeCurrency,
    normalizeLanguage,
    type CurrencyCode,
    type LanguageCode,
} from "@lib/i18n/languages";

export const SETTINGS_QUERY_KEY = ["owner", "settings"] as const;

export type SanitizedSettings = Omit<OwnerSettings, "language" | "currency"> & {
    language: LanguageCode;
    currency: CurrencyCode;
};

/** The server accepts any string for language/currency, so read it defensively. */
export function sanitizeSettings(raw: Partial<OwnerSettings> | undefined): SanitizedSettings {
    return {
        language: normalizeLanguage(raw?.language),
        currency: normalizeCurrency(raw?.currency),
        theme: raw?.theme === "dark" ? "dark" : "light",
        emailNotifications: raw?.emailNotifications ?? true,
        pushNotifications: raw?.pushNotifications ?? true,
    };
}

/**
 * Keeps the app's language and currency in step with the saved account settings. Theme is a
 * per-device choice (the toggle in the header) so it is not forced from the server on load.
 */
export function useServerSettingsSync() {
    const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
    const setLanguage = useLanguageStore((s) => s.setLanguage);
    const setCurrency = useCurrencyStore((s) => s.setCurrency);

    const query = useQuery({
        queryKey: SETTINGS_QUERY_KEY,
        enabled: isAuthenticated,
        staleTime: 5 * 60_000,
        queryFn: async () => sanitizeSettings((await settingsApi.get()).settings),
    });

    React.useEffect(() => {
        if (!query.data) return;
        setLanguage(query.data.language);
        setCurrency(query.data.currency);
    }, [query.data, setLanguage, setCurrency]);

    return query;
}

export function useOwnerSettingsMutation() {
    const { t } = useTranslation();
    const qc = useQueryClient();
    const { success, error } = useToast();
    const setLanguage = useLanguageStore((s) => s.setLanguage);
    const setCurrency = useCurrencyStore((s) => s.setCurrency);

    return useMutation({
        mutationFn: (patch: Partial<OwnerSettings>) => settingsApi.update(patch),
        onSuccess: (res, patch) => {
            const saved = sanitizeSettings(res.settings);
            qc.setQueryData(SETTINGS_QUERY_KEY, saved);
            setLanguage(saved.language);
            setCurrency(saved.currency);
            if (Object.keys(patch).length > 1) success(t("settings.saved"));
        },
        onError: (e: unknown) =>
            error(t("settings.saveFailed"), { description: getApiErrorMessage(e) }),
    });
}
